import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ProtectedRoute from 'src/routes/ProtectedRoute';
import PageNotFound from 'src/shared/components/page-not-found/PageNotFound';
import { getPortfolioRoutesAvailability } from 'src/routes/state/routesSelector';
import { useAppSelector } from 'src/store';
import { IndividualRouteSetting, PortfolioViews } from 'src/routes/routesConfig';
import { Resources } from 'src/shared/types';
import ProductGate from 'src/shared/components/product-gate/ProductGate';
import useCustomer from 'src/app/customers/hooks/useCustomer';
import { ProductKey } from 'src/shared/types/engine/products';
import SiteList from './views/global-nature-risk/site-list/SiteList';
import GnrOverview from './views/global-nature-risk/overview/GnrOverview';
import GnrStateOfNature from './views/global-nature-risk/state-of-nature/GnrStateOfNature';
import GnrImpactsAndDependencies from './views/global-nature-risk/impacts-and-dependencies/GnrImpactsAndDependencies';
import NextSpeciesRiskIndicators from './views/next/NextSpeciesRiskIndicators';
import NextPortfolioSiteExplorer from './views/next/NextPortfolioSiteExplorer';
import NextPortfolioSummary from './views/next/NextPortfolioSummary';

export const portfolioRouteSettings: { [key in PortfolioViews]: IndividualRouteSetting } = {
    portfolioSummary: {
        name: 'Portfolio Summary',
        path: '/',
        handler: <NextPortfolioSummary />,
        resource: Resources.PORTFOLIO_SUMMARY,
    },
    // The grid-backed pages are the portfolio pages (DD-5858). Names keep their
    // Next* prefix until the follow-up rename PR.
    portfolioSiteExplorer: {
        name: 'Portfolio site explorer',
        path: '/site-explorer',
        handler: <NextPortfolioSiteExplorer />,
        resource: Resources.PORTFOLIO_SITE_EXPLORER,
    },
    speciesRiskIndicators: {
        name: 'Portfolio species risks',
        path: '/species-risk-indicators',
        handler: <NextSpeciesRiskIndicators />,
        resource: Resources.PORTFOLIO_SPECIES_RISK_INDICATORS,
    },
    globalNatureRiskOverview: {
        name: 'Global Nature Risk Overview',
        path: '/global-nature-risk/overview',
        handler: <GnrOverview />,
        resource: Resources.PORTFOLIO_RISK_INSIGHTS,
    },
    globalNatureRiskStateOfNature: {
        name: 'Global Nature Risk State of Nature',
        path: '/global-nature-risk/state-of-nature',
        handler: <GnrStateOfNature />,
        resource: Resources.PORTFOLIO_RISK_INSIGHTS,
    },
    globalNatureRiskImpactsAndDependencies: {
        name: 'Global Nature Risk Impacts and Dependencies',
        path: '/global-nature-risk/impacts-and-dependencies',
        handler: <GnrImpactsAndDependencies />,
        resource: Resources.PORTFOLIO_RISK_INSIGHTS,
    },
    globalNatureRiskSiteList: {
        name: 'Global Nature Risk',
        path: '/global-nature-risk/site-list',
        handler: <SiteList />,
        resource: Resources.PORTFOLIO_RISK_INSIGHTS,
    },
};

/** The pages of Global Nature Performance. The rest of the portfolio routes are Global Nature Risk. */
const GLOBAL_NATURE_PERFORMANCE_VIEWS: PortfolioViews[] = [
    PortfolioViews.PORTFOLIO_SUMMARY,
    PortfolioViews.PORTFOLIO_SITE_EXPLORER,
    PortfolioViews.SPECIES_RISK_INDICATORS,
];

/**
 * The /next/* preview paths were shared around in links and bookmarks while the
 * pages were internal; they land on the canonical pages now, params intact.
 * The target is built absolutely from wherever this router is mounted —
 * relative resolution resolves differently across router nestings and appended
 * segments instead of replacing them.
 */
const RedirectToCanonical = ({ to }: { to: string }) => {
    const location = useLocation();
    const mountPath = location.pathname.split('/next/')[0];
    return <Navigate to={{ pathname: `${mountPath}${to}`, search: location.search }} replace />;
};

const Portfolio = () => {
    const routesConfig = useAppSelector(getPortfolioRoutesAvailability);
    const { currentCustomerHasPortfolio } = useCustomer();

    if (!routesConfig) {
        return null;
    }

    return (
        <Routes>
            {Object.keys(routesConfig).map(routeKey => {
                const isAvailable = routesConfig[routeKey as PortfolioViews];
                const routeSetting = portfolioRouteSettings[routeKey as PortfolioViews];

                if (!routeSetting) {
                    return null;
                }

                return (
                    <Route
                        path={routeSetting.path}
                        element={
                            <ProtectedRoute isAllowed={isAvailable} redirectPath={'/customers'} routeSetting={routeSetting}>
                                {GLOBAL_NATURE_PERFORMANCE_VIEWS.includes(routeKey as PortfolioViews) ? (
                                    <ProductGate product={ProductKey.GLOBAL_NATURE_PERFORMANCE} hasResults={currentCustomerHasPortfolio}>
                                        {routeSetting.handler}
                                    </ProductGate>
                                ) : (
                                    routeSetting.handler
                                )}
                            </ProtectedRoute>
                        }
                        key={routeSetting.name}
                    />
                );
            })}
            <Route path='/next/species-risk-indicators' element={<RedirectToCanonical to='/species-risk-indicators' />} />
            <Route path='/next/site-explorer' element={<RedirectToCanonical to='/site-explorer' />} />
            <Route path='/next/summary' element={<RedirectToCanonical to='' />} />
            <Route path='/*' element={<PageNotFound />} />
        </Routes>
    );
};
export default Portfolio;
